import { useCallback } from "react";

import { useRouter } from "next/navigation";

import { getInternalNavigationHref } from "@/utils/getInternalNavigationHref";

type CheckoutNavigationOptions = {
  replace?: boolean;
};

type AppRouter = ReturnType<typeof useRouter>;

const isLeavingHashRouteOnSamePage = (url: string) => {
  const targetUrl = new URL(url, window.location.origin);

  return targetUrl.pathname === window.location.pathname && Boolean(window.location.hash) && !targetUrl.hash;
};

const navigateViaRouter = (router: AppRouter, href: string, replace: boolean) => {
  if (replace) {
    router.replace(href);
    return;
  }

  router.push(href);
};

const navigateViaBrowser = (url: string, replace: boolean) => {
  if (replace) {
    window.location.replace(url);
    return;
  }

  window.location.assign(url);
};

export const useCheckoutNavigationHandler = ({ replace = false }: CheckoutNavigationOptions = {}) => {
  const router = useRouter();

  return useCallback(
    (url: string) => {
      const internalHref = getInternalNavigationHref(url, window.location.origin);

      if (internalHref && !isLeavingHashRouteOnSamePage(url) && !window.location.hash) {
        navigateViaRouter(router, internalHref, replace);
        return;
      }

      navigateViaBrowser(url, replace);
    },
    [router, replace]
  );
};
